import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getJiraAssignableUsers,
  getJiraFieldOptions,
  getJiraFields,
  getJiraIssueOptions,
  getJiraIssueTypes,
  getJiraProjects,
} from 'features/admin-monitoring/config/jira/async';
import { MonitoringJiraState } from 'features/admin-monitoring/config/jira/types';
import {
  jiraProjectQueryCacheKey,
  jiraFieldOptionsCacheKey,
  jiraFieldsCacheKey,
} from 'features/admin-monitoring/config/jira/utils/cache-key-utils';
import {
  fulfilled,
  initialRequestState,
  keyedRequestFulfilled,
  keyedRequestPending,
  keyedRequestRejected,
  pending,
  rejected,
  requestReducer,
} from 'features/shared/request';

export const initialState: MonitoringJiraState = {
  projects: { data: undefined, request: initialRequestState },
  issueTypes: {},
  fields: {},
  assignableUsers: {},
  fieldOptions: {},
  issueOptions: {},
};

const slice = createSlice({
  name: 'monitoringJira',
  initialState,
  reducers: {
    jiraFieldsCleared(state, { payload: key }: PayloadAction<string>) {
      delete state.fields[key];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJiraProjects.pending, (state, { meta }) => {
      state.projects.data = undefined;
      state.projects.request = requestReducer(
        state.projects.request,
        pending({ meta })
      );
    });
    builder.addCase(getJiraProjects.fulfilled, (state, { meta, payload }) => {
      if (state.projects.request.id === meta.requestId) {
        state.projects.data = payload;
        state.projects.request = requestReducer(
          state.projects.request,
          fulfilled({ meta })
        );
      }
    });
    builder.addCase(getJiraProjects.rejected, (state, { meta }) => {
      state.projects.request = requestReducer(
        state.projects.request,
        rejected({ meta })
      );
    });

    builder.addCase(getJiraIssueTypes.pending, (state, { meta }) => {
      keyedRequestPending(state.issueTypes, meta.arg.projectId, meta);
    });
    builder.addCase(getJiraIssueTypes.fulfilled, (state, { meta, payload }) => {
      keyedRequestFulfilled(
        state.issueTypes,
        meta.arg.projectId,
        meta,
        payload
      );
    });
    builder.addCase(getJiraIssueTypes.rejected, (state, { meta }) => {
      keyedRequestRejected(state.issueTypes, meta.arg.projectId, meta);
    });

    builder.addCase(getJiraFields.pending, (state, { meta }) => {
      keyedRequestPending(state.fields, fieldsKey(meta.arg), meta);
    });
    builder.addCase(getJiraFields.fulfilled, (state, { meta, payload }) => {
      keyedRequestFulfilled(state.fields, fieldsKey(meta.arg), meta, payload);
    });
    builder.addCase(getJiraFields.rejected, (state, { meta }) => {
      keyedRequestRejected(state.fields, fieldsKey(meta.arg), meta);
    });

    builder.addCase(getJiraAssignableUsers.pending, (state, { meta }) => {
      keyedRequestPending(
        state.assignableUsers,
        projectQueryKey(meta.arg),
        meta
      );
    });
    builder.addCase(
      getJiraAssignableUsers.fulfilled,
      (state, { meta, payload }) => {
        keyedRequestFulfilled(
          state.assignableUsers,
          projectQueryKey(meta.arg),
          meta,
          payload
        );
      }
    );
    builder.addCase(getJiraAssignableUsers.rejected, (state, { meta }) => {
      keyedRequestRejected(
        state.assignableUsers,
        projectQueryKey(meta.arg),
        meta
      );
    });

    builder.addCase(getJiraFieldOptions.pending, (state, { meta }) => {
      keyedRequestPending(state.fieldOptions, fieldOptionsKey(meta.arg), meta);
    });
    builder.addCase(
      getJiraFieldOptions.fulfilled,
      (state, { meta, payload }) => {
        keyedRequestFulfilled(
          state.fieldOptions,
          fieldOptionsKey(meta.arg),
          meta,
          payload
        );
      }
    );
    builder.addCase(getJiraFieldOptions.rejected, (state, { meta }) => {
      keyedRequestRejected(state.fieldOptions, fieldOptionsKey(meta.arg), meta);
    });

    builder.addCase(getJiraIssueOptions.pending, (state, { meta }) => {
      keyedRequestPending(state.issueOptions, projectQueryKey(meta.arg), meta);
    });
    builder.addCase(
      getJiraIssueOptions.fulfilled,
      (state, { meta, payload }) => {
        keyedRequestFulfilled(
          state.issueOptions,
          projectQueryKey(meta.arg),
          meta,
          payload
        );
      }
    );
    builder.addCase(getJiraIssueOptions.rejected, (state, { meta }) => {
      keyedRequestRejected(state.issueOptions, projectQueryKey(meta.arg), meta);
    });
  },
});

function fieldsKey(arg: { projectId: string; issueTypeId: string }): string {
  return jiraFieldsCacheKey(arg.projectId, arg.issueTypeId);
}

function projectQueryKey(arg: { projectId: string; query: string }): string {
  return jiraProjectQueryCacheKey(arg.projectId, arg.query);
}

function fieldOptionsKey(arg: {
  projectId: string;
  issueTypeId: string;
  fieldId: string;
}): string {
  return jiraFieldOptionsCacheKey(arg.projectId, arg.issueTypeId, arg.fieldId);
}

export const { jiraFieldsCleared } = slice.actions;

const { reducer } = slice;

export default reducer;
